/**
 * The values mirror the root cache keys the hand-written query layer already
 * used, so a migrated domain keeps invalidating and reading the same cache
 * subtree as before. Longest prefix wins: `/planning-proposal/agent` is its own
 * domain, not part of `/planning-proposal`.
 */
const DOMAIN_ROOTS: Record<string, string> = {
  "/access-address": "access-addresses",
  "/account-activity": "account-activity",
  "/audit": "audit",
  "/auth": "auth",
  "/customer": "customer",
  "/cvr": "cvr",
  "/demand": "demand",
  "/environment-restriction": "environment-restriction",
  "/follows": "follows",
  "/momentum": "area-rental-momentum",
  "/municipality": "municipalities",
  "/planning-proposal": "proposals",
  "/planning-proposal/agent": "agents",
  "/planning-proposal/collection": "proposal-collections",
  "/planning-proposal/note": "proposal-notes",
  "/powerbi": "powerbi",
  "/property/address": "property-address",
  "/property/bfe": "property",
  "/property/building": "property-building",
  "/property/cadastre": "cadastre",
  "/property/document": "property-docs",
  "/property/resolve": "property",
  "/property/sfe": "property",
  "/property/street": "property-street",
  "/sales-prices": "sales-prices",
  "/sse": "sse",
  "/unit": "rent-analysis",
  "/user": "user",
  "/user/permission": "staff-permissions",
};

export const domainRootForUrl = (url: string): string => {
  let match: string | undefined;

  for (const prefix of Object.keys(DOMAIN_ROOTS)) {
    const isMatch = url === prefix || url.startsWith(`${prefix}/`);

    if (isMatch && (match === undefined || prefix.length > match.length)) {
      match = prefix;
    }
  }

  if (match === undefined) {
    throw new Error(
      `No domain root mapped for "${url}". Add its URL prefix to DOMAIN_ROOTS in src/api/orval/query-key.ts.`,
    );
  }

  return DOMAIN_ROOTS[match];
};

export const domainQueryKey = (
  properties: Record<string, unknown>,
  // Orval also hands over the caller's `queryOptions` at some call sites; the
  // key is derived from the URL alone.
  { url }: { url: string; queryOptions?: unknown },
) => {
  const root = domainRootForUrl(url);

  return Object.keys(properties).length > 0
    ? ([root, url, properties] as const)
    : ([root, url] as const);
};
